import { Ref, KeyboardEvent, FocusEvent, ChangeEvent } from 'react';
import { useState, useRef, forwardRef, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { TextField as MuiTextField, Tooltip } from '@material-ui/core';

import { useComponentMounted } from '@omq/shared';

import type { ValidationConfig } from '@admin/shared/util/validation';
import { Validation } from '@admin/shared/util/validation';
export type TextFieldProps = {
  tooltip?: string;
  validationConfig?: ValidationConfig;
  onEnter?: () => void;
  setInputRef?: (ref: any) => void | null;
  readOnly?: boolean;
  className?: string;
  errorMessage?: string | null;
  value: string | number;
  onFocus?: (evt: FocusEvent) => void;
  onBlur?: (evt: any) => void;
  onChange?: (evt: ChangeEvent<HTMLInputElement>) => void;
  variant?: 'outlined' | 'standard' | 'filled';
  type?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  InputProps?: any;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  onKeyDownCapture?: (evt: KeyboardEvent) => void;
  inputProps?: any;
  required?: boolean;
  helperText?: string;
  name?: string;
  error?: boolean;
};

const defaultProps = {
  fullWidth: true,
  margin: 'none' as any,
  variant: 'outlined' as any,
};

/**
 * Default input field for Admin UI.
 * Wraps Material ui TextField component.
 *
 * @param {TextFieldProps} props - props passed to mui text field.
 * @param ref - ref, forwarded to mui text field
 *
 * @returns {JSX.Element}
 */
function TextField(
  {
    tooltip,
    onEnter,
    validationConfig = {},
    errorMessage = null,
    setInputRef = null,
    readOnly = false,
    className = null,
    ...textFieldProps
  }: TextFieldProps,
  ref: Ref<HTMLDivElement>,
): JSX.Element {
  const { t } = useTranslation();

  // get custom validation config props
  const { isRequired, pattern, maxLength, minLength } = validationConfig;

  // reference to input DOM element
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [currentErrorMessage, setCurrentErrorMessage] = useState<string | null>(null);
  const enterPressedRef = useRef<boolean>(false);

  useEffect(() => {
    // Pass the inputRef to the parent component
    if (setInputRef) {
      setInputRef(inputRef);
    }
  }, [setInputRef]);

  // check if input value is still valid, if value
  // has been changed, while there is no focus
  // on the field. e.g. during cancel.
  useComponentMounted(() => {
    if (!hasFocus && textFieldProps.value !== '') {
      validateCurrentInput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textFieldProps.value]);

  // validate user input
  const validateCurrentInput = () => {
    // if there is no config available
    // input can't have errors, so we just reset them.
    if (validationConfig == null) {
      return;
    }

    // get error message
    // null if input is valid
    const errorMessage = Validation.validateValueWithConfig(textFieldProps.value, validationConfig);

    // update error state
    setCurrentErrorMessage(errorMessage);
  };

  // store flag if enter key has been pressed
  const handleKeyDown = (evt: KeyboardEvent) => {
    enterPressedRef.current = evt.key === 'Enter';
  };

  const handleKeyUp = (evt: KeyboardEvent) => {
    if (evt.key === 'Enter' && onEnter != null) {
      onEnter();
    }
  };

  // set focus state on focus, to hide error message
  const handleFocus = (evt: FocusEvent) => {
    setHasFocus(true);

    if (textFieldProps.onFocus != null) {
      textFieldProps.onFocus(evt);
    }
  };

  // validate user input when user leaves focus
  const handleBlur = (evt) => {
    setHasFocus(false);
    enterPressedRef.current = false;

    validateCurrentInput();

    if (textFieldProps.onBlur != null) {
      textFieldProps.onBlur(evt);
    }
  };

  // define if error state should be visible
  // do not show errors if field has focus
  // but show error, if enter has been pressed
  // (form was submitted via enter, so validation result needs to be visible)
  const hasError =
    (!hasFocus || enterPressedRef.current) && (currentErrorMessage != null || errorMessage != null);

  // get error message
  let helperText = null;

  if (hasError) {
    if (currentErrorMessage != null) {
      helperText = t(`common:errors.validation.${currentErrorMessage}`);
    } else if (errorMessage != null) {
      helperText = t(`common:errors.validation.${errorMessage}`);
    }
  }

  // component to render
  return (
    <Tooltip title={tooltip != null && !readOnly ? tooltip : ''}>
      <MuiTextField
        {...defaultProps}
        {...textFieldProps}
        ref={ref}
        inputRef={inputRef}
        error={hasError}
        helperText={helperText}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        required={isRequired}
        inputProps={{
          pattern,
          minLength,
          maxLength,
          readOnly,
        }}
        className={className}
      />
    </Tooltip>
  );
}

const Component = forwardRef(TextField);
export { Component as TextField };
